import React, { useEffect, useState } from "react";
import { Card, Button, Table, Popconfirm, Space } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import {
  HomeOutlined,
  DeleteOutlined,
  EditOutlined,
  TagOutlined,
  BarcodeOutlined,
} from "@ant-design/icons";
import {
  getProductTags,
  deleteProductTag,
} from "../../../network/network";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { useTranslation } from "react-i18next";
import AddOption from "./AddOption";
import OptionFormModal from "./OptionFormModal";
import { handleApiError } from "../../../utils/errorHandler";

const ProductTags = () => {
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [reloadTableData, setReloadTableData] = useState(false);
  const [productTagsTableData, setProductTagsTableData] = useState([]);
  const [editOption, setEditOption] = useState(null);
  const { t } = useTranslation();

  const reloadOptions = () => {
    setReloadTableData((prevState) => !prevState);
  };

  useEffect(() => {
    setIsDataLoading(true);
    getProductTags()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setProductTagsTableData(response?.data?.data?.product_tags || []);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [reloadTableData, t]);

  const handleDelete = (id) => {
    deleteProductTag(id)
      .then((response) => {
        const data = response?.data || response?.response?.data;

        if (data?.success === true) {
          reloadOptions();
          NotificationWithIcon(
            "success",
            t("option_deleted"),
            t("option_deleted_successfully"),
          );
        } else if (data?.success === false) {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  };

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "/products",
      breadcrumbName: t("products"),
      icon: <BarcodeOutlined />,
    },
    {
      path: "options",
      breadcrumbName: t("options"),
      icon: <TagOutlined />,
    },
  ];

  const detailsPartColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => {
        return <p>{index + 1}</p>;
      },
    },
    {
      title: t("name_en"),
      render: (record) => {
        return <>{record.name}</>;
      },
    },
    {
      title: t("name_ar"),
      render: (record) => {
        return <>{record.name_ar}</>;
      },
    },
  ];

  const actionPartColumn = [
    {
      title: t("action"),
      width: "100px",
      align: "center",
      render: (record) => {
        return (
          <Space>
            <Button
              type="link"
              onClick={() => setEditOption(record)}
              title={t("option_edit")}
            >
              <EditOutlined />
            </Button>
            <Popconfirm
              title={t("warning_delete")}
              onConfirm={() => handleDelete(record.id)}
              okText={t("yes")}
              cancelText={t("no")}
              placement="topLeft"
            >
              <Button type="link" title={t("delete_entity")}>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("options")}
          ghost={false}
          extra={[
            <AddOption
              key="open-add-option-modal"
              iconOnly={false}
              reloadCallback={reloadOptions}
            />,
          ]}
        />
      </Card>
      <Card>
        <Table
          columns={[...detailsPartColumns, ...actionPartColumn]}
          dataSource={productTagsTableData}
          loading={isDataLoading}
          rowKey="id"
          pagination={false}
        />
      </Card>

      <OptionFormModal
        open={Boolean(editOption)}
        mode="edit"
        initialValues={editOption}
        onClose={() => setEditOption(null)}
        onSuccess={reloadOptions}
      />
    </>
  );
};

export default ProductTags;
